import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Pulse,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  WarningOctagon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin → System Health
 *
 * One-stop dashboard for "is the platform OK right now?". Pulls top-level
 * stats (tracker hit rate, postback fail rate, sync health, daily-email
 * cadence) plus recent alerts. Alerts can be marked resolved inline so the
 * "active" count stays meaningful.
 */
export default function AdminSystemHealth() {
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ category: "", only_active: true });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, a] = await Promise.all([
        API.get("/admin/system/health"),
        API.get("/admin/system/health/alerts", {
          params: { only_active: filter.only_active, category: filter.category || undefined, limit: 100 },
        }),
      ]);
      setHealth(h.data);
      setAlerts(a.data.alerts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resolveAlert = async (id) => {
    try {
      await API.post(`/admin/system/health/alerts/${id}/resolve`);
      toast.success("Alert resolved");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-system-health">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Pulse size={24} weight="bold" className="text-[#002FA7]" />
            System Health
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Tracker, postbacks, sync & daily-email — last 24h. Refreshes on demand.
            {health?.as_of && <span className="ml-2 font-mono">· as of {new Date(health.as_of).toLocaleTimeString()}</span>}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="refresh-health">
          <ArrowsClockwise size={14} className="mr-1.5" /> {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi
          label="Tracker (24h)"
          big={`${health?.tracker?.total_24h ?? "—"}`}
          sub={`${health?.tracker?.errors_24h ?? 0} errors · ${health?.tracker?.error_rate ?? 0}%`}
          good={(health?.tracker?.error_rate ?? 0) < 1}
        />
        <Kpi
          label="Postbacks (24h)"
          big={`${health?.postback?.total_24h ?? "—"}`}
          sub={`${health?.postback?.failed_24h ?? 0} failed · ${health?.postback?.fail_rate ?? 0}%`}
          good={(health?.postback?.fail_rate ?? 0) < 5}
        />
        <Kpi
          label="Sync runs (24h)"
          big={`${health?.sync?.runs_24h ?? "—"}`}
          sub="Skimlinks / mrge / YK"
          good={(health?.sync?.runs_24h ?? 0) >= 3}
        />
        <Kpi
          label="Daily email"
          big={health?.daily_email?.healthy ? "OK" : "STALE"}
          sub={health?.daily_email?.last_sent
            ? `Last: ${new Date(health.daily_email.last_sent).toLocaleString()}`
            : "Never sent"}
          good={Boolean(health?.daily_email?.healthy)}
        />
      </div>

      {/* Active alerts */}
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="alerts-panel">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <WarningOctagon size={18} weight="bold" className="text-rose-600" />
            <h2 className="text-sm font-bold text-slate-900">
              Active alerts <span className="text-slate-400 font-normal">({alerts.length})</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <select
              value={filter.category}
              onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
              className="border border-slate-200 rounded px-2 py-1 bg-white text-xs"
              data-testid="alert-category-filter"
            >
              <option value="">All categories</option>
              <option value="tracker">Tracker</option>
              <option value="postback">Postback</option>
              <option value="sync">Sync</option>
              <option value="daily_email">Daily email</option>
            </select>
            <label className="flex items-center gap-1.5 text-slate-600">
              <input
                type="checkbox"
                checked={filter.only_active}
                onChange={(e) => setFilter((f) => ({ ...f, only_active: e.target.checked }))}
                data-testid="alert-only-active"
              />
              Only active
            </label>
          </div>
        </div>

        {alerts.length === 0 && (
          <div className="p-12 text-center text-sm text-slate-400">
            <CheckCircle size={32} className="mx-auto mb-2 text-emerald-400" weight="bold" />
            No alerts in this view.
          </div>
        )}

        {alerts.map((a) => {
          const sevColor = {
            warn: "border-l-amber-500 bg-amber-50/30",
            error: "border-l-rose-500 bg-rose-50/30",
            critical: "border-l-rose-700 bg-rose-100/40",
          }[a.severity] || "border-l-slate-400";
          return (
            <div
              key={a.id}
              className={`p-4 border-b border-slate-100 border-l-4 ${sevColor}`}
              data-testid={`alert-row-${a.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-[0.15em] font-bold ${
                      a.severity === "warn" ? "text-amber-700" : "text-rose-700"
                    }`}>
                      {a.severity} · {a.category}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">{a.key}</span>
                    {a.suppressed_count > 0 && (
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        +{a.suppressed_count} more
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-900 font-medium">{a.message}</div>
                  {a.details && Object.keys(a.details).length > 0 && (
                    <div className="mt-2 text-[11px] font-mono text-slate-600 bg-white border border-slate-100 rounded px-2 py-1.5 max-w-2xl overflow-x-auto">
                      {Object.entries(a.details).map(([k, v]) => (
                        <div key={k}><span className="text-slate-400">{k}:</span> {String(v)}</div>
                      ))}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-2">
                    First: {a.created_at && new Date(a.created_at).toLocaleString()}
                    {a.last_event_at && a.last_event_at !== a.created_at && (
                      <> · Last: {new Date(a.last_event_at).toLocaleString()}</>
                    )}
                    {a.resolved_at && (
                      <> · <span className="text-emerald-600">Resolved {new Date(a.resolved_at).toLocaleString()}</span></>
                    )}
                  </div>
                </div>
                {!a.resolved_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resolveAlert(a.id)}
                    className="h-7 text-[11px] shrink-0"
                    data-testid={`resolve-${a.id}`}
                  >
                    Mark resolved
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, big, sub, good }) {
  return (
    <div className={`bg-white border rounded-lg p-4 ${good ? "border-slate-200" : "border-rose-200"}`}>
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold font-heading ${good ? "text-slate-900" : "text-rose-700"}`}>{big}</div>
      <div className={`text-[11px] mt-1 ${good ? "text-slate-500" : "text-rose-600"}`}>
        {good ? <CheckCircle size={11} weight="bold" className="inline mr-1 text-emerald-500" /> : <Warning size={11} weight="bold" className="inline mr-1" />}
        {sub}
      </div>
    </div>
  );
}
